import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Minus, Trash2, TreePine, Pencil, Check, X, MoreVertical, ChevronRight } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";
import { queueOp, queueAndFlush, getQueuedDeletedIds, getQueuedCreates } from "@/lib/syncQueue";
import { toast } from "sonner";
import { API } from "@/lib/api";

export function LumberInventory({ readOnly }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lumber_cache') || '[]'); } catch { return []; }
  });
  const [showAdd, setShowAdd] = useState(false);
  const [newThickness, setNewThickness] = useState("3.5");
  const [newWidth, setNewWidth] = useState("6");
  const [newLength, setNewLength] = useState("8");
  const [newDesc, setNewDesc] = useState("Poplar");
  const [newQty, setNewQty] = useState("");
  const [adjustAmounts, setAdjustAmounts] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({ thickness: "3.5", width: "6", length: "8", description: "", quantity: "" });
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lumber_history_cache') || '[]'); } catch { return []; }
  });
  const [undoEntry, setUndoEntry] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const { data: serverData } = await axios.get(`${API}/lumber-inventory`);
      let data = serverData;
      try {
        const deletedIds = getQueuedDeletedIds('lumber');
        if (deletedIds.length > 0) data = data.filter(i => !deletedIds.includes(i.id));
        getQueuedCreates('lumber').forEach(body => {
          if (body?.id && !data.find(i => i.id === body.id)) data.push(body);
        });
      } catch {}
      setItems(data);
    } catch {}
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lumber-inventory/history`);
      setHistory(data);
    } catch {}
  }, []);

  useEffect(() => { fetchItems(); fetchHistory(); }, [fetchItems, fetchHistory]);

  // Persist locally so lumber counts survive restarts and open offline
  useEffect(() => {
    try { localStorage.setItem('lumber_cache', JSON.stringify(items)); } catch {}
  }, [items]);
  useEffect(() => {
    try { localStorage.setItem('lumber_history_cache', JSON.stringify(history)); } catch {}
  }, [history]);

  const addItem = async () => {
    const thickness = parseFloat(newThickness) || null;
    const length = parseFloat(newLength) || null;
    if (!length) { toast.error("Length is required"); return; }
    const newItem = {
      id: crypto.randomUUID(),
      thickness,
      width: newWidth || "6",
      length,
      description: newDesc || null,
      quantity: parseFloat(newQty) || 0,
    };
    setNewThickness("3.5"); setNewWidth("6"); setNewLength("8"); setNewDesc("Poplar"); setNewQty("");
    setShowAdd(false);
    try {
      await axios.post(`${API}/lumber-inventory`, newItem);
      await fetchItems();
      toast.success("Lumber added");
    } catch {
      setItems(prev => [...prev, newItem]);
      queueOp({ method: "post", path: "/lumber-inventory", body: newItem, entity: "lumber", entityId: newItem.id });
      toast.success("Lumber added");
    }
  };

  const adjust = async (id, amount) => {
    // Optimistic: update the count immediately; queue the relative adjustment offline
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: Math.round(((i.quantity || 0) + amount) * 100) / 100 } : i));
    try {
      const { data } = await axios.put(`${API}/lumber-inventory/${id}/adjust`, { amount });
      setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: data.quantity } : i));
      fetchHistory();
    } catch {
      queueOp({ method: "put", path: `/lumber-inventory/${id}/adjust`, body: { amount }, entity: "lumber", entityId: id });
    }
  };

  const handleCustomAdjust = (id, add) => {
    const val = parseFloat(adjustAmounts[id] || "") || 0;
    if (val <= 0) return;
    adjust(id, add ? val : -val);
    setAdjustAmounts(prev => ({ ...prev, [id]: "" }));
  };

  const deleteItem = async () => {
    if (!deleteId) return;
    setItems(prev => prev.filter(i => i.id !== deleteId));
    // Write-ahead: deletion recorded on device first — can never resurrect
    queueAndFlush({ method: "delete", path: `/lumber-inventory/${deleteId}`, entity: "lumber", entityId: deleteId });
    toast.success("Deleted");
    setDeleteId(null);
  };

  const undoHistoryEntry = async () => {
    if (!undoEntry) return;
    try {
      await axios.post(`${API}/lumber-inventory/history/undo`, { timestamp: undoEntry.timestamp, item_id: undoEntry.item_id });
      setUndoEntry(null);
      await fetchItems();
      await fetchHistory();
      toast.success("Undone");
    } catch { toast.error("Failed to undo"); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditFields({ thickness: item.thickness || "3.5", width: item.width || "6", length: item.length || "8", description: item.description || "", quantity: item.quantity || 0 });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const length = parseFloat(editFields.length) || null;
    if (!length) { toast.error("Length is required"); return; }
    const body = {
      thickness: parseFloat(editFields.thickness) || null,
      width: editFields.width || "6",
      length,
      description: editFields.description || null,
      quantity: parseFloat(editFields.quantity) || 0,
    };
    const id = editingId;
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...body } : i));
    setEditingId(null);
    try {
      await axios.put(`${API}/lumber-inventory/${id}`, body);
      await fetchItems();
      toast.success("Updated");
    } catch {
      queueOp({ method: "put", path: `/lumber-inventory/${id}`, body, entity: "lumber", entityId: id });
      toast.success("Updated");
    }
  };

  const WOOD_TYPES = ["Pine", "Southern Yellow Pine", "Poplar", "Hardwood"];
  const speciesOrder = ["Pine", "Southern Yellow Pine", "Poplar", "Hardwood", "Other"];

  // Group items by thickness, then by species within each thickness
  const grouped = {};
  items.forEach(item => {
    const key = item.thickness ? `${item.thickness}"` : 'Other';
    if (!grouped[key]) grouped[key] = {};
    // Normalize species: match to known types or 'Other'
    const raw = (item.description || '').trim();
    const match = WOOD_TYPES.find(w => w.toLowerCase() === raw.toLowerCase());
    const species = match || (raw ? raw : 'Other');
    if (!grouped[key][species]) grouped[key][species] = [];
    grouped[key][species].push(item);
  });
  // Sort groups by thickness value
  const groupEntries = Object.entries(grouped).sort((a, b) => {
    const aVal = parseFloat(a[0]) || 999;
    const bVal = parseFloat(b[0]) || 999;
    return aVal - bVal;
  });
  // Sort items within each species group: thickness → width → length
  const sortItems = (arr) => arr.sort((a, b) => {
    const tDiff = (parseFloat(a.thickness) || 0) - (parseFloat(b.thickness) || 0);
    if (tDiff !== 0) return tDiff;
    const aW = a.width === 'Random' ? 999 : (parseFloat(a.width) || 0);
    const bW = b.width === 'Random' ? 999 : (parseFloat(b.width) || 0);
    const wDiff = aW - bW;
    if (wDiff !== 0) return wDiff;
    return (parseFloat(a.length) || 0) - (parseFloat(b.length) || 0);
  });

  const renderItem = (item) => (
    <div key={item.id} className="border-b border-zinc-100 px-2 py-2" data-testid={`lumber-item-${item.id}`}>
      {editingId === item.id ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={editFields.quantity} onChange={e => setEditFields(p => ({ ...p, quantity: e.target.value.slice(0, 12) }))} placeholder="Qty" className="w-16 h-7 rounded-none text-xs text-center border-zinc-300" />
          <select value={editFields.thickness} onChange={e => setEditFields(p => ({ ...p, thickness: e.target.value }))} className="h-7 rounded-none text-xs border border-zinc-300 bg-white px-1">
            <option value="">—</option>
            <option value="1.5">1.5&quot;</option>
            <option value="3.5">3.5&quot;</option>
            <option value="5.5">5.5&quot;</option>
            <option value="6">6&quot;</option>
          </select>
          <span className="text-zinc-400 text-xs">x</span>
          <select value={editFields.width || "6"} onChange={e => setEditFields(p => ({ ...p, width: e.target.value }))} className="h-7 rounded-none text-xs border border-zinc-300 bg-white px-1">
            <option value="6">6&quot;</option>
            <option value="8">8&quot;</option>
            <option value="Random">Rndm</option>
          </select>
          <span className="text-zinc-400 text-xs">x</span>
          <select value={editFields.length} onChange={e => setEditFields(p => ({ ...p, length: e.target.value }))} className="h-7 rounded-none text-xs border border-zinc-300 bg-white px-1">
            <option value="">—</option>
            <option value="2">2&apos;</option>
            <option value="4">4&apos;</option>
            <option value="5">5&apos;</option>
            <option value="6">6&apos;</option>
            <option value="7">7&apos;</option>
            <option value="8">8&apos;</option>
            <option value="10">10&apos;</option>
            <option value="12">12&apos;</option>
            <option value="14">14&apos;</option>
            <option value="16">16&apos;</option>
          </select>
          <select value={editFields.description} onChange={e => setEditFields(p => ({ ...p, description: e.target.value }))} className="h-7 rounded-none text-xs border border-zinc-300 bg-white px-1" data-testid={`edit-desc-${item.id}`}>
            <option value="">Species</option>
            {WOOD_TYPES.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
          <button onClick={saveEdit} className="p-1 text-zinc-600 hover:text-zinc-900" data-testid={`save-edit-${item.id}`}><Check className="w-3.5 h-3.5" /></button>
          <button onClick={() => setEditingId(null)} className="p-1 text-zinc-400 hover:text-zinc-600"><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className={`text-2xl font-bold min-w-[50px] text-right ${item.quantity < 0 ? 'text-red-600' : 'text-zinc-900'}`} style={{ fontFamily: "Chivo, sans-serif" }} data-testid={`lumber-qty-${item.id}`}>
            {Math.round(item.quantity).toLocaleString()}
          </span>
          <span className="text-sm text-zinc-600 flex-1">
            {item.thickness ? `${item.thickness}"` : ''}{item.width && item.width !== 'Random' ? ` x ${item.width}"` : item.width === 'Random' ? ' x Rndm' : ''} x {item.length}&apos;{item.description ? ` ${item.description}` : ''}
          </span>
          {!readOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 text-zinc-400 hover:text-zinc-600" data-testid={`lumber-menu-${item.id}`}><MoreVertical className="w-3.5 h-3.5" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-zinc-300 min-w-[140px]">
              <div className="px-3 py-2 border-b border-zinc-100">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">Adjust Quantity</p>
                <div className="flex items-center gap-2">
                  <input type="number" min="1" value={adjustAmounts[item.id] || ""} onChange={e => setAdjustAmounts(prev => ({ ...prev, [item.id]: e.target.value }))} placeholder="#" className="w-14 h-6 text-[11px] text-center border border-zinc-200 bg-white focus:border-zinc-400 focus:outline-none" data-testid={`adjust-input-${item.id}`} />
                  <button onClick={() => handleCustomAdjust(item.id, true)} className="text-xs font-bold px-2 h-6 border border-zinc-200 text-zinc-600 hover:bg-zinc-100" data-testid={`add-qty-${item.id}`}>+</button>
                  <button onClick={() => handleCustomAdjust(item.id, false)} className="text-xs font-bold px-2 h-6 border border-zinc-200 text-zinc-600 hover:bg-zinc-100" data-testid={`sub-qty-${item.id}`}>-</button>
                </div>
              </div>
              <DropdownMenuItem onClick={() => startEdit(item)} className="rounded-none text-xs cursor-pointer" data-testid={`edit-lumber-${item.id}`}>
                <Pencil className="w-3 h-3 mr-2" />Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDeleteId(item.id)} className="text-red-600 focus:text-red-600 focus:bg-red-50 rounded-none text-xs cursor-pointer" data-testid={`delete-lumber-${item.id}`}>
                <Trash2 className="w-3 h-3 mr-2" />Delete
              </DropdownMenuItem>
              <DropdownMenuItem className="rounded-none text-xs cursor-pointer text-zinc-500">
                <X className="w-3 h-3 mr-2" />Cancel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
    <div className="space-y-4">
      {/* Add Lumber box */}
      <Card className="bg-white border border-zinc-200 rounded-none shadow-none" data-testid="lumber-inventory-card">
        <CardHeader className="p-4 pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              Lumber Inventory
            </CardTitle>
            {!readOnly && (
            <Button onClick={() => setShowAdd(!showAdd)} size="sm" className="rounded-none bg-zinc-800 hover:bg-zinc-700 text-white h-7 text-xs" data-testid="add-lumber-btn">
              <Plus className="w-3 h-3 mr-1" />
              Add Lumber
            </Button>
            )}
          </div>
        </CardHeader>
        {showAdd && (
          <CardContent className="p-4 pt-0">
            <div className="border border-zinc-200 p-3 space-y-3" data-testid="add-lumber-form">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1 block">Thickness</label>
                  <select value={newThickness} onChange={e => setNewThickness(e.target.value)} className="w-full h-8 rounded-none text-sm border border-zinc-300 bg-white px-1" data-testid="new-lumber-thickness">
                    <option value="">—</option>
                    <option value="1.5">1.5&quot;</option>
                    <option value="3.5">3.5&quot;</option>
                    <option value="5.5">5.5&quot;</option>
                    <option value="6">6&quot;</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1 block">Width</label>
                  <select value={newWidth} onChange={e => setNewWidth(e.target.value)} className="w-full h-8 rounded-none text-sm border border-zinc-300 bg-white px-1" data-testid="new-lumber-width">
                    <option value="6">6&quot;</option>
                    <option value="8">8&quot;</option>
                    <option value="Random">Random</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1 block">Length</label>
                  <select value={newLength} onChange={e => setNewLength(e.target.value)} className="w-full h-8 rounded-none text-sm border border-zinc-300 bg-white px-1" data-testid="new-lumber-length">
                    <option value="">—</option>
                    <option value="2">2&apos; Cutoff</option>
                    <option value="4">4&apos;</option>
                    <option value="5">5&apos;</option>
                    <option value="6">6&apos;</option>
                    <option value="7">7&apos;</option>
                    <option value="8">8&apos;</option>
                    <option value="10">10&apos;</option>
                    <option value="12">12&apos;</option>
                    <option value="14">14&apos;</option>
                    <option value="16">16&apos;</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1 block">Species</label>
                  <select value={newDesc} onChange={e => setNewDesc(e.target.value)} className="w-full h-8 rounded-none text-sm border border-zinc-300 bg-white px-2" data-testid="new-lumber-desc">
                    <option value="">Select</option>
                    <option value="Pine">Pine</option>
                    <option value="Southern Yellow Pine">Southern Yellow Pine</option>
                    <option value="Poplar">Poplar</option>
                    <option value="Hardwood">Hardwood</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1 block">Qty</label>
                  <Input value={newQty} onChange={e => setNewQty(e.target.value.slice(0, 12))} placeholder="0" className="rounded-none h-8 text-sm" data-testid="new-lumber-qty" />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={addItem} size="sm" className="rounded-none bg-zinc-800 hover:bg-zinc-700 text-white h-8 text-xs" data-testid="save-lumber-btn">Save</Button>
                  <Button onClick={() => setShowAdd(false)} variant="outline" size="sm" className="rounded-none h-8 text-xs border-zinc-300" data-testid="cancel-lumber-btn">Cancel</Button>
                </div>
              </div>
            </div>
          </CardContent>
        )}
        {items.length === 0 && !showAdd && (
          <CardContent className="p-4 pt-0">
            <p className="text-sm text-zinc-400 text-center py-4">No lumber yet.</p>
          </CardContent>
        )}
      </Card>

      {/* Thickness group boxes */}
      {groupEntries.map(([thickness, speciesGroups]) => (
        <Card key={thickness} className="bg-white border border-zinc-200 rounded-none shadow-none" data-testid={`lumber-group-${thickness}`}>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-center text-3xl font-black text-zinc-900 uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
              {thickness} Thick
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {speciesOrder.filter(s => speciesGroups[s]).map(species => (
              <div key={species} className="mb-3 last:mb-0">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-semibold mb-1 mt-2">{species}</p>
                <div className="space-y-0">
                  {sortItems([...speciesGroups[species]]).map(renderItem)}
                </div>
              </div>
            ))}
            {/* Any species not in the predefined order */}
            {Object.keys(speciesGroups).filter(s => !speciesOrder.includes(s)).map(species => (
              <div key={species} className="mb-3 last:mb-0">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-semibold mb-1 mt-2">{species}</p>
                <div className="space-y-0">
                  {sortItems([...speciesGroups[species]]).map(renderItem)}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Inventory Total */}
      {items.length > 0 && (() => {
        const totalBdFt = items.reduce((sum, item) => {
          const t = parseFloat(item.thickness) || 0;
          const w = item.width === 'Random' ? 6 : (parseFloat(item.width) || 0);
          const l = parseFloat(item.length) || 0;
          const qty = item.quantity || 0;
          return sum + (qty * t * w * l) / 12;
        }, 0);
        return (
          <div className="p-4 bg-zinc-900 text-white" data-testid="inventory-total">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold whitespace-nowrap">Total Inventory Board Feet</p>
              <p className="text-2xl font-bold text-blue-400 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
                {totalBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        );
      })()}

      {/* History */}
      {history.length > 0 && (
        <Card className="bg-white border border-zinc-200 rounded-none shadow-none" data-testid="lumber-history">
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="w-full p-4 flex items-center justify-between"
            data-testid="history-toggle"
          >
            <span className="text-sm font-bold text-zinc-700 uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
              History
              <span className="text-xs font-normal text-zinc-400 ml-2">({history.length})</span>
            </span>
            <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${historyExpanded ? 'rotate-90' : ''}`} />
          </button>
          {historyExpanded && (
          <CardContent className="p-4 pt-0">
            <div className="space-y-0">
              {history.map((h, i) => (
                <div key={i} className="border-b border-zinc-100 py-1.5 flex items-center gap-2 text-xs">
                  <span className={`font-bold min-w-[40px] text-right ${h.amount > 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {h.amount > 0 ? '+' : ''}{h.amount}
                  </span>
                  <span className="text-zinc-500">
                    {h.thickness ? `${h.thickness}"` : ''}{h.width && h.width !== 'Random' ? ` x ${h.width}"` : h.width === 'Random' ? ' x Rndm' : ''} x {h.length}&apos;{h.description ? ` ${h.description}` : ''}
                  </span>
                  <span className="text-zinc-400 ml-auto text-[10px]">
                    {h.auto && <span className="text-violet-400 mr-1">auto</span>}
                    {new Date(h.timestamp).toLocaleDateString()} {new Date(h.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {!h.auto && !readOnly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-0.5 text-zinc-300 hover:text-zinc-500"><MoreVertical className="w-3 h-3" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-none border-zinc-300 min-w-[100px]">
                      <DropdownMenuItem onClick={() => setUndoEntry(h)} className="rounded-none text-xs cursor-pointer">
                        Undo
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
          )}
        </Card>
      )}
    </div>

    <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Delete Lumber?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">This will permanently remove this lumber from your inventory.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={deleteItem} className="bg-red-600 hover:bg-red-700 text-white rounded-none">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={!!undoEntry} onOpenChange={() => setUndoEntry(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Undo this change?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            {undoEntry && (<>This will reverse <strong>{undoEntry.amount > 0 ? '+' : ''}{undoEntry.amount}</strong> on {undoEntry.thickness ? `${undoEntry.thickness}"` : ''}{undoEntry.width && undoEntry.width !== 'Random' ? ` x ${undoEntry.width}"` : undoEntry.width === 'Random' ? ' x Rndm' : ''} x {undoEntry.length}&apos;{undoEntry.description ? ` ${undoEntry.description}` : ''}.</>)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={undoHistoryEntry} className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-none">Undo</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
